import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/shared/lib/utils"
import { Slot } from "radix-ui"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-controle font-sans text-sm font-medium whitespace-nowrap transition-[background-color,color] duration-200 ease-in-out outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border border-primary bg-transparent text-brass-light hover:bg-primary hover:text-background",
        destructive:
          "border border-destructive bg-transparent text-destructive hover:bg-destructive hover:text-background focus-visible:ring-destructive/20",
        // `outline`, `secondary` e `ghost` resultam no mesmo contorno em
        // `--line-strong`, o mesmo que serve às ações de linha de tabela.
        // Ajustar uma sem as outras duas faz as três variantes divergirem sem
        // que nenhum chamador tenha pedido isso.
        outline:
          "border border-linha-forte bg-transparent text-texto-medio hover:bg-superficie-2 hover:text-foreground",
        secondary:
          "border border-linha-forte bg-transparent text-texto-medio hover:bg-superficie-2 hover:text-foreground",
        ghost:
          "border border-linha-forte bg-transparent text-texto-medio hover:bg-superficie-2 hover:text-foreground",
        link: "border-transparent bg-transparent text-inherit underline-offset-4 hover:underline",
      },
      size: {
        // Cada tamanho define a altura pelo padding, nunca por uma classe
        // `h-*`: um botão de altura fixa cortaria o texto sempre que a fonte
        // do navegador viesse maior que o esperado.
        default: "px-5 py-[11px] has-[>svg]:px-4",
        // `xs` e `sm` resultam no mesmo tamanho: as chamadas de `Button`
        // espalhadas pelos módulos usam as duas indistintamente para a mesma
        // ação de linha de tabela, e diferenciá-las agora faria metade delas
        // mudar de tamanho sem que o chamador tivesse pedido.
        xs: "gap-1 px-3 py-1.5 text-xs has-[>svg]:px-2.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "gap-1 px-3 py-1.5 text-xs has-[>svg]:px-2.5 [&_svg:not([class*='size-'])]:size-3",
        lg: "px-6 py-3.5 text-[15px] has-[>svg]:px-5",
        icon: "size-9",
        "icon-xs": "size-6 rounded-controle [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    // `destructive` já nasce pequeno, independente de qualquer outro tamanho
    // que o chamador peça: é a única variante que embute o próprio tamanho.
    // Sem este `compoundVariants`, um `destructive` com `size="lg"` ou sem
    // `size` (o padrão é `default`) sairia do padding grande dos demais
    // botões, e um botão vermelho grande nunca aparece nesta tela.
    compoundVariants: [
      {
        variant: "destructive",
        size: ["default", "lg"],
        class: "px-3 py-1.5 text-xs",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
